import React from 'react';
// utils
import { useSelector } from 'react-redux';
// components
import Navbar from 'components/Navbar';
import Text from 'components/Text';
// styles
import classnames from 'classnames/bind';
import styles from './PrivacyPolicy.module.scss';
import Logo from 'components/Logo';
import Link from 'components/Link';

const cn = classnames.bind(styles);

const PrivacyPolicy = () => {
  const { email } = useSelector(({ auth }) => auth.user);

  return (
    <>
      {!!email && <Navbar />}
      {!email && (
        <Link to="/">
          <Logo type="vFull" className={cn('logo')} />
        </Link>
      )}
      <section className={cn('container')}>
        <div className={cn('wrapper')}>
          <Text type="h1" className="mb-40">
            Privacy Policy
          </Text>
          <Text className={cn('text', 'mb-30')}>
            Thank you for visiting a Site owned and operated by Sesame Workshop or an App published by Sesame Workshop (from which you have linked to this Privacy Policy). “Sites” refers to Internet web sites and “Apps” refers to apps for use on mobile communication devices such as smartphones and tablets. “Platforms” refers to Sites and Apps collectively. Through the Platforms, Sesame Workshop aims to extend our mission to use educational media to help children reach their highest potential.
          </Text>
          <Text className={cn('text', 'mb-70')}>
            “Sesame Workshop” also is referred to as “we,” “us” or “our” in this Privacy Policy
          </Text>
          <Text type="h4" className="mb-15">
            Privacy Principles
          </Text>
          <Text className={cn('text', 'mb-30')}>
            Sesame Workshop has been trusted by parents for decades to provide safe, entertaining and educational content. We take this trust seriously and recognize the importance of privacy concerns especially in the digital environment.
          </Text>
          <Text className={cn('text', 'mb-30')}>
            With this in mind, we have adopted this Privacy Policy for our Platforms. On our Platforms, we collect personal information in two forms. One form is information that we expressly request you to provide in connection with certain activities. The second form is anonymous information that we automatically collect from your computer, phone or other device.
          </Text>
          <Text className={cn('text', 'mb-70')}>Our Privacy Policy is based on the following principles:</Text>
          <ol>
            <li>
              <Text className={cn('text', 'mb-20')}>
                You are free to choose whether or not to engage in any activity on our Platforms that involves your submission of personal information, such as your name, email address, phone number or other information that can be used to identify or contact an individual, whether online or offline. We will ask you for such information expressly. Most of the activities on our Platforms do not require you to submit any personal information.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                When we ask you to submit personal information, we will explain in the sign-up or collection process how we will use the information (such as requesting an email address when you sign up for a newsletter). We will use the information only as we have stated, other than in Special Circumstances which are described below (such as in relation to law enforcement or a business transfer).
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                We use the anonymous information that is automatically collected from your device, such as a computer’s Internet Protocol address, a mobile device identifier, operating system, or general location such as zip code, to support internal operations so that we can understand and monitor the usage of our Platforms and improve the performance of the Platforms.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                There is no behavioral advertising on our Platforms which means that you will not see any advertising that is targeted to you based on your combined activity on our Platforms and your activity on other sites and apps.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                Third parties that we use to assist with the Platforms are required to follow this Privacy Policy.
                ESPECIALLY FOR CHILDREN UNDER 13
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                Our collection of personal information on Platforms that are directed to children under 13 (“Children’s Platforms”) is intended to follow the principles of the Children’s Online Privacy Protection Act (“COPPA”), a U.S. law designed to protect the online privacy of children under the age of 13.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                We will obtain parental consent before asking for personal information on our Children’s Platforms, unless the request for information falls within an exception that would be permitted under COPPA.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                We do not make personal information collected from children publicly available nor do we enable children to so on our Platforms. We will not condition a child’s participation in any activity or service on the disclosure of more personal information than is reasonably necessary to participate in an activity.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                Parents may contact us (at dotorgmail@sesame.org) to review, update or delete any of their children’s personal information that we may have collected and to elect for us not to collect any additional personal information from their children.
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mb-20')}>
                We will update this Privacy Policy from time to time by posting the updated Privacy Policy online. We will not change how we handle previously collected information without providing notice and obtaining parental consent where necessary.
              </Text>
            </li>
          </ol>
          <Text className={cn('text', 'mv-70')}>
            The following describes our Privacy Policy as it relates to certain specific activities.
          </Text>
          <Text type="h4" className="mb-15">
            INFORMATION WE REQUEST ON OUR CHILDREN’S PLATFORMS
          </Text>
          <Text className={cn('text', 'mb-30')}>
            We will obtain parental consent before asking for personal information on our Children’s Platforms, unless the request for information falls within an exception that would be permitted under COPPA. We will explain how we will use the information and use it only for that purpose. Here are examples of personal information that we request on our Children’s Platforms:
          </Text>
          <Text className={cn('text', 'mb-30')}>
            • Share With a Friend. Visitors can provide their first name, a friend’s first name and friend’s email address to send a feature of a Children’s Platform to the friend via email. We use such information only to fulfill the visitor’s request and do not store that information for any longer than reasonably necessary to send the relevant email.
          </Text>
          <Text className={cn('text', 'mb-70')}>
            • Email Newsletters. When visitors on our Children’s Platforms wish to sign up to receive our email newsletters, we will ask for a parent’s first name and email address. We will send a notification email to the parent and give the parent the option to decline the newsletter. If the newsletter is declined, we will not send the newsletter and will delete from our files any related personal information collected in the sign-up process. If the newsletter is not declined, we will send the newsletter. All newsletters contain instructions so that the recipient may unsubscribe at any time.
          </Text>
          <Text type="h4" className="mb-15">
            INFORMATION WE COLLECT AUTOMATICALLY
          </Text>
          <Text className={cn('text', 'mb-70')}>
            We use the anonymous information that is automatically collected to support the internal operations of the Platforms. Some of this information is collected through third-party analytics tools. Internal operations include the following:
          </Text>
          <ul>
            <li>
              <Text className={cn('text', 'mt-20')}>
                to monitor metrics such as total number of visitors and traffic;
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mt-20')}>to understand which activities and services are most popular;</Text>
            </li>
            <li>
              <Text className={cn('text', 'mt-20')}>
                to understand the pages where visitors enter our Sites and the pages viewed;
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mt-20')}>
                to reduce the repetition in the promotional messages that users see on our Platforms;
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mt-20')}>
                to understand generally the location of our users (e.g., zip code information);
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mt-20')}>
                to recognize the browsers used to visit our Sites, so that the content is adapted for the relevant browser; and
              </Text>
            </li>
            <li>
              <Text className={cn('text', 'mt-20')}>to diagnose and fix technology problems.</Text>
            </li>
          </ul>
          <Text className={cn('text', 'mv-70')}>
            When you visit a Platform, we may use cookies and similar technologies like pixels and web beacons, to collect this anonymous information for the purpose of the internal operations of our Platforms. A “cookie” is a piece of information that a web site or an app transfers to a user’s device for record-keeping purposes. As a user moves from one page or one screen to another, a cookie is used to tell us that it is the same device. We also may use a cookie to keep track of your selections. You may set your device to accept or not accept cookies and you may delete any cookies that you have received. However, should you decide to disable any cookies we place on your device, you may not be able to use certain services or features on our Platforms. A “web beacon” is a one-pixel image (which may be visible or transparent), which may be embedded on a web page or e-mail message, and which may also provide us with information about usage of that page or e-mail message.
          </Text>
          <Text type="h4" className="mb-15">
            Special Circumstances
          </Text>
          <Text className={cn('text', 'mb-30')}>
            Law Enforcement and Safety. We may access, preserve and share your information when we have a good faith belief that it is necessary to: (i) respond to a legal request (like a search warrant, court order or subpoena) or otherwise to take action because we believe it is required or advisable under the law; (ii) detect, prevent and address fraud and other illegal activity; (iii) respond to your requests for customer service; or (iv) limit our legal liability or to protect the rights, property, or personal safety of ourselves, any user of our Platforms, a third party or the general public including as part of investigations.
          </Text>
          <Text className={cn('text', 'mb-30')}>
            Business Transfer. If we sell or otherwise transfer part or the whole of Sesame Workshop or our assets to another organization (e.g., in the course of a transaction like a merger, acquisition, bankruptcy, dissolution, liquidation), your personal information, and any other information collected through our Platforms may be among the items sold or transferred. The buyer or transferee will be required to honor the commitments we have made in this Privacy Policy.
          </Text>
          <Text className={cn('text', 'mb-70')}>
            Security Notice. In the unlikely event that we believe that the security of your personal information in our possession or control may have been compromised, we may seek to notify you of that development. If a notification is appropriate, we would endeavor to do so as promptly as possible under the circumstances, and, to the extent you have given us your e-mail address, we may notify you by e-mail. You consent to our use of e-mail as a means of such notification.
          </Text>
          <Text type="h4" className="mb-15">
            Service Providers
          </Text>
          <Text className={cn('text', 'mb-70')}>
            We contract with third parties to provide certain services to facilitate the operations of our Platforms (“service providers”), such as to manage the analytics information about usage of our Platforms or to process donations. Our service providers will be given access to your information only as is reasonably necessary to provide the services for which they are contracted. They will be required to abide by the terms of this Privacy Policy in their handling of personal information.
          </Text>
          <Text type="h4" className="mb-15">
            Where We Store your Information
          </Text>
          <Text className={cn('text', 'mb-70')}>
            The Platforms are governed by and operated in, and in accordance with the laws of, the United States, and are intended for the enjoyment of residents of the United States.  Your information collected through a Platform may be stored and processed in the United States or any other country in which we or our service providers maintain facilities. We or our service providers may transfer information that we collect about you, including personal information across borders and from your country or jurisdiction to other countries or jurisdictions around the world.
          </Text>
          <Text type="h4" className="mb-15">
            Security of your information
          </Text>
          <Text className={cn('text', 'mb-70')}>
            We strive to maintain the security of information but cannot guarantee that information security measures are fail-safe or guarantee that information on a Platform may not be accessed, disclosed, altered, or destroyed. Our Platforms maintain technical and organizational steps to keep information collected secure, including limiting the number of people who have physical access to our database servers and use of electronic security systems and password protections to guard against unauthorized access.
          </Text>
          <Text type="h4" className="mb-15">
            Other Websites and Services
          </Text>
          <Text className={cn('text', 'mb-30')}>
            Our Platforms may link to other websites or apps, including content offered by third parties that we do not control and whose privacy and data collection practices may differ from ours (“Third Party Platforms”). We are not responsible for the practices employed by any websites or services linked to or from our Platforms, including the information or content contained within them. When you go via our link to Third Party Platforms, you become subject to their terms of use and privacy policies rather than ours. You should review these other website privacy policies regarding any personal information that they may collect about you.
          </Text>
          <Text type="h4" className="mb-15">
            Online Safety
          </Text>
          <Text className={cn('text', 'mb-70')}>
            Involvement in your family’s online lives is one of the most important safety tools available. In addition, we encourage you to educate yourself and your child on privacy concerns. We recommend, as a starting point, the following guide published by the federal government,{' '}
            <a href="http://www.onguardonline.gov/articles/0031-kids-privacy">
              http://www.onguardonline.gov/articles/0031-kids-privacy
            </a>.
          </Text>
          <Text type="h4" className="mb-15">
            Sesame Workshop Information
          </Text>
          <Text className={cn('text', 'mb-30')}>
            The Platforms are operated by Sesame Workshop, located at 1900 Broadway, New York, New York, 10023, USA,{' '}
            <a href="tel:212-262-3456">212-595-3456</a>,{' '}
            <a href="mailto:dotorgmail@sesameworkshop.org">dotorgmail@sesameworkshop.org</a>. 
          </Text>
          <Text className={cn('text', 'mb-30')}>
            For questions or concerns about the Platforms, please write to <a href="mailto:dotorgmail@sesameworkshop.org">dotorgmail@sesameworkshop.org</a>. To report potential security vulnerabilities, please write to <a href="mailto:cyber.security@sesame.org">cyber.security@sesame.org</a>.
          </Text>
          <Text className={cn('text', 'mb-70')}>
            <strong>Effective date: June 1, 2022.</strong>
          </Text>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
